import { CommonModule, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { inject, isDevMode, LOCALE_ID, NgModule } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AuthComponentsModule, AuthGmsAngularClientModule } from '@coin/shared/auth/feature';
import { AuthGmsClientConfig } from '@coin/shared/auth/util';
import { LibGbrModule } from '@coin/shared/gbr/feature';
import { ApiModule, Configuration as ApiConfiguration } from '@coin/shared/core/data-access';
import { ApiModule as ApiModuleV2, Configuration as ApiConfigurationV2 } from '@coin/shared/core/data-access-v2';
import { FeatureFlagDirective } from '@coin/shared/core/feature-directives';
import { LegacyComponentsModule } from '@coin/shared/core/feature-legacy-components';
import { NumberFormatCountryIso } from '@coin/shared/core/util-enums';
import { environment } from '@coin/shared/core/util-environments';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import {
  AppEnvironment,
  CustomTranslationLoader,
  provideTranslateDebugStore
} from '@coin/shared/core/data-access-translation-loader';
import { NgxsModule } from '@ngxs/store';
import { UpdateNotifierComponent } from '@coin/shared/core/ui-storybook';
import { provideDefaultDateFormats } from '@coin/shared/core/util-helpers';
import { AdminAppState } from './admin-app-state';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { httpInterceptorProviders } from './http-interceptors';
import { SharedModule } from './shared/shared.module';
import { HeaderComponent } from './static/header/header.component';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

registerLocaleData(localeDe, NumberFormatCountryIso.German, localeDeExtra);

@NgModule({
  declarations: [AppComponent, HeaderComponent],
  bootstrap: [AppComponent],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    LegacyComponentsModule,
    AppRoutingModule,
    AuthComponentsModule,
    AuthGmsAngularClientModule.forRoot({
      ...environment.authentication,
      ...environment.adminAuthentication
    } as AuthGmsClientConfig),
    NgxsModule.forRoot(AdminAppState, {
      developmentMode: !environment.production,
      selectorOptions: { suppressErrors: true }
    }),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production
    }),
    TranslateModule.forRoot(CustomTranslationLoader.createConfig(AppEnvironment.Admin)),
    ApiModule.forRoot(() => {
      return new ApiConfiguration({ basePath: environment.api.baseUrl });
    }),
    ApiModuleV2.forRoot(() => {
      return new ApiConfigurationV2({ basePath: environment.api.baseUrl });
    }),
    LibGbrModule,
    FeatureFlagDirective,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWithDelay:5000'
    }),
    UpdateNotifierComponent
  ],
  providers: [
    { provide: LOCALE_ID, useValue: NumberFormatCountryIso.German },
    TranslateModule,
    TranslateService,
    httpInterceptorProviders,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideDefaultDateFormats(),
    provideTranslateDebugStore(),
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } } // fix for https://jira.siemens-energy.com/browse/GMTEC-22822
  ]
})
export class AppModule {
  private matIconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);

  constructor() {
    this.loadOperatorIcons();
  }

  private addSvgIcon(name: string): void {
    this.matIconRegistry.addSvgIcon(
      name,
      this.domSanitizer.bypassSecurityTrustResourceUrl(`../../../assets/svgs/operators/${name}.svg`)
    );
  }

  private loadOperatorIcons(): void {
    [
      'contains',
      'endswith',
      'equal',
      'greater',
      'greaterorequal',
      'less',
      'lessorequal',
      'notequal',
      'dropdocuments',
      'notstartswith',
      'notcontains'
    ].map(iconName => this.addSvgIcon(iconName));
  }
}
